import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Pencil, User, Phone, MessageCircle, Mail, Home, MapPin, Briefcase, GraduationCap, Church, RefreshCw, Trash2, ExternalLink } from "lucide-react";
import { Input } from "../components/ui/input";
import { friendlyErr } from "../lib/friendlyErr";
import { api, fileUrl, errMsg } from "../lib/api";
import { namaGelar, gelar } from "../lib/utils";
import { useAuth } from "../lib/auth";
import { isoToDisplay, isoToDisplayDateTime } from "../components/DateInput";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Avatar, AvatarImage, AvatarFallback } from "../components/ui/avatar";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import StatusBadge from "../components/StatusBadge";
import SearchableSelect from "../components/SearchableSelect";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { FotoLightbox } from "../components/FotoLightbox";
import { DokumenJemaat } from "../components/DokumenJemaat";
import { KONDISI, KondisiBadge } from "./KondisiAnggotaPage";

export default function JemaatDetailPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const auth = useAuth();
  const canWrite = auth.can("jemaat:write");
  const canDelete = auth.can("jemaat:delete");
  const showKondisi = auth.user?.role === "ADMIN" || auth.user?.role === "PIMPINAN";
  const [kondisi, setKondisi] = useState(null);
  const [zoom, setZoom] = useState(null);
  useEffect(() => { if (showKondisi && id) api.get(`/kondisi/${id}`).then((r) => setKondisi(r.data)).catch(() => setKondisi(null)); }, [showKondisi, id]);
  const [deleteStep, setDeleteStep] = useState(0);
  const [deleteText, setDeleteText] = useState("");
  const [deleting, setDeleting] = useState(false);
  const doDelete = async () => {
    if (deleting || deleteText !== "HAPUS") return;
    setDeleting(true);
    try {
      await api.delete(`/jemaat/${id}`, { data: { konfirmasi: deleteText } });
      toast.success("Data jemaat berhasil dipindahkan ke Data Jemaat Terhapus.");
      setDeleteStep(0);
      navigate("/jemaat");
    } catch (e) {
      toast.error(friendlyErr(e, { 401: "Sesi Anda telah berakhir. Silakan login kembali.", 403: "Anda tidak memiliki hak untuk menghapus data jemaat.", 409: "Data jemaat sudah terhapus.", 422: "Konfirmasi penghapusan tidak valid." }));
    } finally { setDeleting(false); }
  };
  const [j, setJ] = useState(null);
  const [master, setMaster] = useState({});
  const [statusOpen, setStatusOpen] = useState(false);
  const [newStatus, setNewStatus] = useState("");
  const [statusNote, setStatusNote] = useState("");
  const [saving, setSaving] = useState(false);

  const load = () => api.get(`/jemaat/${id}`).then((r) => setJ(r.data)).catch((e) => toast.error(errMsg(e)));

  useEffect(() => {
    load();
    api.get("/master/options").then((r) => setMaster(r.data)).catch(() => {});
  }, [id]);

  const changeStatus = async () => {
    if (!newStatus) { toast.error("Pilih status baru"); return; }
    setSaving(true);
    try {
      await api.patch(`/jemaat/${id}/status`, { status_jemaat: newStatus, alasan: statusNote || null });
      toast.success("Status jemaat diperbarui");
      setStatusOpen(false);
      setStatusNote("");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  if (!j) return <div className="text-center py-16 text-slate-400">Memuat...</div>;

  return (
    <div className="max-w-4xl mx-auto space-y-5">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" className="rounded-xl" onClick={() => navigate("/jemaat")} data-testid="back-btn">
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <h1 className="text-xl sm:text-2xl font-bold tracking-tight text-slate-900">Detail Jemaat</h1>
      </div>

      <Card className="p-5 sm:p-6 rounded-2xl border-slate-200/80 shadow-sm" data-testid="detail-hero">
        <div className="flex flex-col sm:flex-row gap-5">
          <Avatar className={`h-24 w-24 rounded-2xl border border-slate-200 mx-auto sm:mx-0 ${j.foto_path ? "cursor-zoom-in hover:ring-2 hover:ring-[hsl(224,64%,33%)]/40" : ""}`} role={j.foto_path ? "button" : undefined} title={j.foto_path ? "Klik untuk memperbesar foto" : undefined} onClick={() => j.foto_path && setZoom({ src: fileUrl(j.foto_path), alt: j.nama_lengkap })} data-testid="detail-foto">
            {j.foto_path ? <AvatarImage src={fileUrl(j.foto_path)} alt={j.nama_lengkap} className="object-cover" /> : null}
            <AvatarFallback className="rounded-2xl bg-slate-100 text-slate-400">
              <User className="h-10 w-10" />
            </AvatarFallback>
          </Avatar>
          <div className="flex-1 text-center sm:text-left">
            <h2 className="text-xl font-bold text-slate-900" data-testid="detail-nama">{namaGelar(j)}</h2>
            {j.nama_panggilan && <p className="text-sm text-slate-500">"{j.nama_panggilan}"</p>}
            <p className="font-mono text-sm text-slate-500 mt-1" data-testid="detail-nij">NIJ: {j.nij}</p>
            {j.nij_riwayat?.length > 0 && <p className="text-xs text-slate-400 mt-0.5" data-testid="detail-nij-riwayat">NIJ sebelumnya: {j.nij_riwayat.map((r) => r.nij).filter(Boolean).join(", ")}</p>}
            <div className="flex flex-wrap gap-2 mt-3 justify-center sm:justify-start">
              <StatusBadge value={j.status_jemaat} type="jemaat" testId="detail-status-jemaat" />
              <StatusBadge value={j.status_keanggotaan} type="keanggotaan" />
              {j.status_keanggotaan === "Jemaat Pindahan" && j.asal_gereja && <span className="text-xs px-2.5 py-1 rounded-full bg-indigo-50 text-indigo-700 font-semibold" data-testid="detail-asal-gereja">Asal: {j.asal_gereja}{j.tanggal_pindah ? ` · pindah ${isoToDisplay(j.tanggal_pindah)}` : ""}</span>}
              {j.komisi && <span className="text-xs px-2.5 py-1 rounded-full bg-slate-100 text-slate-600 font-semibold">{j.komisi}</span>}
            </div>
          </div>
          {canWrite && !j.is_deleted && <div className="flex sm:flex-col gap-2 justify-center">
            <Button className="rounded-xl bg-[hsl(224,64%,33%)] hover:bg-[hsl(224,64%,28%)]" onClick={() => navigate(`/jemaat/${id}/edit`)} data-testid="edit-jemaat-btn">
              <Pencil className="h-4 w-4 mr-2" /> Edit
            </Button>
            <Button variant="outline" className="rounded-xl" onClick={() => { setNewStatus(j.status_jemaat); setStatusOpen(true); }} data-testid="change-status-btn">
              <RefreshCw className="h-4 w-4 mr-2" /> Ubah Status
            </Button>
            {canDelete && <Button variant="outline" className="rounded-xl border-red-200 text-red-600 hover:bg-red-50" onClick={() => { setDeleteStep(1); setDeleteText(""); }} data-testid="delete-jemaat-btn">
              <Trash2 className="h-4 w-4 mr-2" /> Hapus Jemaat
            </Button>}
          </div>}
          {j.is_deleted && (
            <div className="w-full sm:w-auto text-sm bg-red-50 border border-red-200 rounded-xl px-4 py-3 space-y-1" data-testid="deleted-banner">
              <div className="font-bold text-red-700 tracking-wide">DATA JEMAAT TELAH DIHAPUS</div>
              <div className="text-slate-700">Nama: <b>{namaGelar(j)}</b></div>
              <div className="text-slate-700">NIJ: <b className="font-mono">{j.nij}</b></div>
              <div className="text-slate-700">Tanggal dihapus: {isoToDisplayDateTime(j.deleted_at)}</div>
              <div className="text-slate-700">Dihapus oleh: {j.deleted_by || "-"}</div>
              <div className="text-slate-700">Status: <span className="font-semibold text-red-700">Terhapus</span></div>
              <Button variant="outline" size="sm" className="rounded-xl mt-1 min-h-[40px]" onClick={() => navigate("/jemaat/terhapus")} data-testid="goto-terhapus-btn">Buka Jemaat Terhapus</Button>
            </div>
          )}
        </div>
      </Card>

      <Dialog open={deleteStep > 0} onOpenChange={(o) => !o && setDeleteStep(0)}>
        <DialogContent className="rounded-2xl max-w-[calc(100vw-2rem)] sm:max-w-md" data-testid="delete-dialog">
          <DialogHeader>
            <DialogTitle>{deleteStep === 1 ? "Hapus Data Jemaat?" : "Konfirmasi Penghapusan"}</DialogTitle>
            <DialogDescription asChild>
              <div className="space-y-2 text-sm text-slate-600">
                {deleteStep === 1 ? (
                  <>
                    <p>Anda akan menghapus data jemaat berikut:</p>
                    <p className="rounded-xl bg-slate-50 p-3"><span className="block">Nama: <b className="text-slate-900">{namaGelar(j)}</b></span><span className="block">NIJ: <b className="font-mono text-slate-900">{j.nij}</b></span></p>
                    <p>Data tidak akan dihapus secara permanen. Data akan dipindahkan ke Data Jemaat Terhapus dan masih dapat dipulihkan oleh Admin.</p>
                    <p className="font-semibold text-amber-700">Pastikan Anda yakin sebelum melanjutkan.</p>
                  </>
                ) : (
                  <>
                    <p>Untuk menghapus data jemaat, ketik <b className="font-mono text-slate-900">HAPUS</b> pada kotak di bawah.</p>
                    <p className="text-xs text-slate-500">Data akan tetap tersimpan dalam sistem dan dapat dipulihkan oleh Admin.</p>
                  </>
                )}
              </div>
            </DialogDescription>
          </DialogHeader>
          {deleteStep === 2 && <Input value={deleteText} onChange={(e) => setDeleteText(e.target.value)} placeholder="Ketik HAPUS" className="min-h-[44px] rounded-xl font-mono" data-testid="delete-confirm-input" autoFocus autoComplete="off" />}
          <DialogFooter className="flex-row gap-2">
            <Button variant="outline" className="rounded-xl min-h-[44px] flex-1" onClick={() => setDeleteStep(0)} disabled={deleting} data-testid="delete-cancel-btn">Batal</Button>
            {deleteStep === 1
              ? <Button className="rounded-xl min-h-[44px] flex-1 bg-red-600 hover:bg-red-700" onClick={() => setDeleteStep(2)} data-testid="delete-next-btn">Lanjutkan</Button>
              : <Button className="rounded-xl min-h-[44px] flex-1 bg-red-600 hover:bg-red-700" disabled={deleteText !== "HAPUS" || deleting} onClick={doDelete} data-testid="delete-confirm-btn">{deleting ? "Menghapus..." : "Hapus Jemaat"}</Button>}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Tabs defaultValue={j.fields_terbatas ? "terbatas" : "pribadi"}>
        {j.fields_terbatas ? (
          <div className="rounded-xl bg-slate-100 text-slate-600 px-3 py-2 text-xs" data-testid="jemaat-akses-terbatas">Anda melihat data jemaat dengan akses terbatas sesuai peran Anda.</div>
        ) : (
        <TabsList className={`w-full grid grid-cols-2 ${showKondisi ? "sm:grid-cols-7" : "sm:grid-cols-6"} h-auto rounded-xl bg-slate-100 p-1`}>
          <TabsTrigger value="pribadi" className="rounded-lg text-xs sm:text-sm" data-testid="tab-pribadi">Data Pribadi</TabsTrigger>
          <TabsTrigger value="kontak" className="rounded-lg text-xs sm:text-sm" data-testid="tab-kontak">Alamat & Kontak</TabsTrigger>
          <TabsTrigger value="gereja" className="rounded-lg text-xs sm:text-sm" data-testid="tab-gereja">Gerejawi</TabsTrigger>
          <TabsTrigger value="pekerjaan" className="rounded-lg text-xs sm:text-sm" data-testid="tab-pekerjaan">Pekerjaan</TabsTrigger>
          <TabsTrigger value="riwayat" className="rounded-lg text-xs sm:text-sm" data-testid="tab-riwayat">Riwayat Status</TabsTrigger>
          <TabsTrigger value="dokumen" className="rounded-lg text-xs sm:text-sm" data-testid="tab-dokumen">Dokumen</TabsTrigger>
          {showKondisi && <TabsTrigger value="kondisi" className="rounded-lg text-xs sm:text-sm" data-testid="tab-kondisi">Kondisi {kondisi?.status ? (KONDISI[kondisi.status]?.emoji || "") : ""}</TabsTrigger>}
        </TabsList>
        )}
        {j.fields_terbatas && (
          <Card className="p-5 rounded-2xl space-y-1 mt-3" data-testid="jemaat-terbatas-card">
            <Row label="NIJ" value={j.nij} mono />
            <Row label="Nama Panggilan" value={j.nama_panggilan} />
            <Row label="Jenis Kelamin" value={j.jenis_kelamin} />
            <Row label="Status Jemaat" value={j.status_jemaat} />
            {j.status_keanggotaan !== undefined && <Row label="Status Keanggotaan" value={j.status_keanggotaan} />}
            {j.status_keanggotaan === "Jemaat Pindahan" && <Row label="Asal Gereja" value={j.asal_gereja} />}
            <Row icon={Phone} label="Nomor HP" value={j.nomor_hp} mono />
            <Row icon={MessageCircle} label="WhatsApp" value={j.whatsapp} mono />
            {j.bidang_pelayanan !== undefined && <Row label="Bidang Pelayanan" value={(j.bidang_pelayanan || []).join(", ") || null} />}
            {j.alamat !== undefined && <>
              {[["Alamat sesuai KTP", j.alamat_ktp, "alamat-ktp"], ["Alamat Domisili", j.alamat_domisili, "alamat-domisili"]].map(([lbl, a, tid]) => a && Object.values(a).some(Boolean) && (
                <div key={tid} className="mt-3 pt-3 border-t" data-testid={`detail-${tid}`}>
                  <div className="text-xs font-bold uppercase tracking-wide text-[hsl(224,64%,33%)] mb-1">{lbl}</div>
                  <Row icon={Home} label="Detail" value={a.detail} /><Row label="Kelurahan/Desa" value={a.kelurahan} /><Row label="Kecamatan" value={a.kecamatan} /><Row label="Kabupaten/Kota" value={a.kota} /><Row label="Provinsi" value={a.provinsi} /><Row label="Kode Pos" value={a.kode_pos} mono />
                </div>))}
            </>}
          </Card>
        )}

        {showKondisi && <TabsContent value="kondisi">
          <Card className="p-5 rounded-2xl mt-3 space-y-4" data-testid="kondisi-tab-card">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div><div className="text-xs text-slate-500">Status Kondisi Saat Ini</div><div className="mt-1"><KondisiBadge status={kondisi?.status} testId="jemaat-kondisi-status" /></div></div>
              <div className="text-sm text-slate-600">Care Leader: <b data-testid="jemaat-kondisi-cl">{kondisi?.care_leader?.nama || "Belum memiliki Care Leader"}</b></div>
            </div>
            {kondisi?.catatan && <div className="rounded-xl bg-slate-50 border p-3 text-sm" data-testid="jemaat-kondisi-catatan">{kondisi.catatan}<div className="text-xs text-slate-500 mt-1">Diperbarui {isoToDisplayDateTime(kondisi.waktu)} oleh {kondisi.diubah_oleh_nama || kondisi.diubah_oleh}</div></div>}
            <div>
              <div className="font-semibold text-sm mb-2">Riwayat Kondisi</div>
              {(!kondisi?.riwayat || kondisi.riwayat.length === 0) ? <p className="text-sm text-slate-400" data-testid="jemaat-kondisi-riwayat-kosong">Belum ada riwayat kondisi.</p> : (
                <ol className="space-y-3" data-testid="jemaat-kondisi-riwayat">{kondisi.riwayat.map((r) => (
                  <li key={r.id} className="border-l-2 border-[hsl(224,64%,33%)]/40 pl-4 py-1" data-testid={`jemaat-kondisi-riwayat-${r.id}`}>
                    <div className="flex flex-wrap items-center gap-2 text-sm"><KondisiBadge status={r.status_lama} /><span className="text-slate-400">→</span><KondisiBadge status={r.status_baru} /></div>
                    <p className="text-sm text-slate-700 mt-1">{r.catatan}</p>
                    <p className="text-xs text-slate-500 mt-1">Oleh: {r.oleh_nama ? `${r.oleh_nama} (${r.oleh})` : r.oleh} · {isoToDisplayDateTime(r.waktu)}</p>
                  </li>))}</ol>)}
            </div>
          </Card>
        </TabsContent>}

        <TabsContent value="riwayat">
          <Card className="p-5 rounded-2xl mt-3" data-testid="riwayat-status-list">
            {(!j.riwayat_status || j.riwayat_status.length === 0) ? (
              <p className="text-sm text-slate-400">Belum ada riwayat perubahan status.</p>
            ) : (
              <ol className="space-y-3">
                {j.riwayat_status.map((r) => (
                  <li key={r.id} className="border-l-2 border-[hsl(224,64%,33%)]/40 pl-4 py-1" data-testid={`riwayat-item-${r.id}`}>
                    <div className="flex flex-wrap items-center gap-2 text-sm">
                      <StatusBadge value={r.status_lama || "—"} type="jemaat" />
                      <span className="text-slate-400">→</span>
                      <StatusBadge value={r.status_baru} type="jemaat" />
                    </div>
                    {r.alasan && <p className="text-sm text-slate-700 mt-1">Alasan: {r.alasan}</p>}
                    <p className="text-xs text-slate-500 mt-1">Diubah oleh: {r.diubah_oleh_nama ? `${r.diubah_oleh_nama} (${r.diubah_oleh})` : r.diubah_oleh} · {isoToDisplayDateTime(r.waktu)}</p>
                  </li>
                ))}
              </ol>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="pribadi">
          <Card className="p-5 rounded-2xl space-y-1 mt-3">
            <Row label="Jenis Kelamin" value={j.jenis_kelamin} />
            {j.status_keanggotaan === "Jemaat Pindahan" && <Row label="Asal Gereja" value={j.asal_gereja} />}
            {j.status_keanggotaan === "Jemaat Pindahan" && <Row label="Tanggal Pindah" value={j.tanggal_pindah ? isoToDisplay(j.tanggal_pindah) : null} />}
            {!j.sensitif_disamarkan && <Row label="NIK" value={j.nik} mono />}
            {!j.sensitif_disamarkan && <Row label="Nomor KK" value={j.nomor_kk} mono />}
            <Row label="Tempat Lahir" value={j.tempat_lahir} />
            <Row label="Tanggal Lahir" value={isoToDisplay(j.tanggal_lahir)} />
            {ulangTahunInfo(j.tanggal_lahir) && <div data-testid="detail-ulang-tahun"><Row label={ulangTahunInfo(j.tanggal_lahir).label} value={ulangTahunInfo(j.tanggal_lahir).value} /></div>}
            <Row label="Status Pernikahan" value={j.status_pernikahan} />
            {j.status_pernikahan === "Menikah" && <Row label="Tanggal Pernikahan" value={isoToDisplay(j.tanggal_pernikahan)} />}
            {j.status_pernikahan === "Menikah" && <Row label="Tempat Pernikahan" value={j.tempat_pernikahan} />}
            {j.status_pernikahan === "Menikah" && <ParentRow label="Suami/Istri" nama={j.nama_pasangan} linked={j.pasangan} navigate={navigate} testId="detail-pasangan" />}
          </Card>
        </TabsContent>

        <TabsContent value="kontak">
          <Card className="p-5 rounded-2xl space-y-1 mt-3">
            {[["Alamat sesuai KTP", j.alamat_ktp, "alamat-ktp"], ["Alamat Domisili", j.alamat_domisili, "alamat-domisili"]].map(([lbl, a, tid]) => (
              <div key={tid} className="pb-3 mb-2 border-b" data-testid={`kontak-${tid}`}>
                <div className="text-xs font-bold uppercase tracking-wide text-[hsl(224,64%,33%)] mb-1">{lbl}</div>
                {a && Object.values(a).some(Boolean) ? (<>
                  <Row icon={Home} label="Detail" value={a.detail} /><Row label="Kelurahan/Desa" value={a.kelurahan} /><Row label="Kecamatan" value={a.kecamatan} /><Row label="Kabupaten/Kota" value={a.kota} /><Row label="Provinsi" value={a.provinsi} /><Row label="Kode Pos" value={a.kode_pos} mono />
                </>) : <p className="text-sm text-slate-400">-</p>}
              </div>))}
            <Row icon={Phone} label="Nomor HP" value={j.nomor_hp} mono />
            <Row icon={MessageCircle} label="WhatsApp" value={j.whatsapp} mono />
            <Row icon={Mail} label="Email" value={j.email} />
            <Row icon={MapPin} label="Link Google Maps" value={j.link_google_maps ? (
              <span className="flex flex-wrap items-center justify-end gap-2">
                <a href={j.link_google_maps} target="_blank" rel="noreferrer" className="text-[hsl(224,64%,33%)] hover:underline break-all text-xs" data-testid="detail-link-google-maps">{j.link_google_maps}</a>
                <Button asChild size="sm" className="rounded-xl min-h-[36px] bg-[hsl(224,64%,33%)] hover:bg-[hsl(224,64%,28%)]"><a href={j.link_google_maps} target="_blank" rel="noreferrer" data-testid="detail-buka-maps-btn"><ExternalLink className="h-3.5 w-3.5 mr-1" /> Buka di Google Maps</a></Button>
              </span>) : null} />
          </Card>
        </TabsContent>

        <TabsContent value="gereja">
          <Card className="p-5 rounded-2xl space-y-1 mt-3">
            <Row icon={Church} label="Status Baptisan" value={j.status_baptisan} />
            {j.status_baptisan === "Sudah" && <Row label="Tanggal Baptisan" value={isoToDisplay(j.tanggal_baptisan)} />}
            {j.status_baptisan === "Sudah" && <Row label="Tempat Baptisan" value={j.tempat_baptisan} />}
            <Row label="Komisi" value={j.komisi} />
            <Row label="Jabatan Gerejawi" value={j.jabatan_gerejawi ? `${j.jabatan_gerejawi} (${gelar(j.jabatan_gerejawi)})` : null} />
            {j.komisi === "Komisi Anak" && j.kelas_sm && <Row label="Kelas SM" value={j.kelas_sm} />}
            <Row label="Care Leader" value={j.care_leader} />
            <Row label="Bidang Pelayanan" value={j.bidang_pelayanan?.length ? <Chips items={j.bidang_pelayanan} testId="detail-bidang-pelayanan" cls="bg-indigo-50 text-indigo-700" /> : null} />
            {["Keyboard","Guitar","Bass","Drum"].some((i) => j.bidang_pelayanan?.includes(i)) && <Row label="Instrumen Lain yang Dikuasai" value={j.jenis_musik?.length ? <Chips items={j.jenis_musik} testId="detail-jenis-musik" cls="bg-emerald-50 text-emerald-700" /> : null} />}
            {j.bidang_pelayanan?.includes("Lainnya") && <Row label="Bidang Pelayanan Lainnya" value={j.pelayanan_lainnya} />}
            <Row label="Keterangan" value={j.keterangan} />
            <div className="pt-3 mt-2 border-t border-slate-100 space-y-2" data-testid="orang-tua-section">
              <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Data Orang Tua</div>
              <ParentRow label="Ayah" nama={j.nama_ayah} linked={j.ayah} navigate={navigate} testId="detail-ayah" />
              <ParentRow label="Ibu" nama={j.nama_ibu} linked={j.ibu} navigate={navigate} testId="detail-ibu" />
            </div>
            {j.anak?.length > 0 && (
              <div className="pt-3 mt-2 border-t border-slate-100 space-y-2" data-testid="hubungan-keluarga-section">
                <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Hubungan Keluarga — Anak</div>
                {j.anak.map((a, i) => (
                  <button key={a.id} type="button" onClick={() => navigate(`/jemaat/${a.id}`)} data-testid={`detail-anak-${a.id}`}
                    className="w-full flex items-center justify-between gap-2 p-2.5 rounded-xl border border-slate-100 hover:bg-slate-50 text-left min-h-[44px]">
                    <span className="text-sm text-slate-800 font-medium">{i + 1}. {namaGelar(a)} <span className="font-mono text-xs text-slate-500">· {a.nij}</span></span>
                    <StatusBadge value={a.status_jemaat} type="jemaat" />
                  </button>
                ))}
              </div>
            )}
            {j.keluarga && <div className="pt-3 mt-2 border-t border-slate-100">
              <Button variant="outline" className="rounded-xl w-full sm:w-auto" onClick={() => navigate(`/keluarga/${j.keluarga_id}`)} data-testid="goto-keluarga-btn">
                <Home className="h-4 w-4 mr-2" /> Lihat Keluarga: {j.keluarga.nama_keluarga}
              </Button>
            </div>}
          </Card>
        </TabsContent>

        <TabsContent value="dokumen">
          <Card className="p-5 rounded-2xl mt-3" data-testid="tab-dokumen-content"><DokumenJemaat jemaatId={j.id} /></Card>
        </TabsContent>
        <TabsContent value="pekerjaan">
          <Card className="p-5 rounded-2xl space-y-1 mt-3">
            <Row icon={GraduationCap} label="Pendidikan" value={j.pendidikan} />
            <Row icon={Briefcase} label="Pekerjaan" value={j.pekerjaan} />
            <Row label="Instansi" value={j.instansi} />
            <Row label="Jabatan" value={j.jabatan} />
          </Card>
        </TabsContent>
      </Tabs>

      <Dialog open={statusOpen} onOpenChange={setStatusOpen}>
        <DialogContent className="rounded-2xl">
          <DialogHeader>
            <DialogTitle>Ubah Status Jemaat</DialogTitle>
            <DialogDescription>Pilih status baru untuk jemaat ini. Data historis tetap tersimpan.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-500">Data historis jemaat tetap tersimpan. Ubah status tidak menghapus data.</p>
            <SearchableSelect options={master.status_jemaat} value={newStatus} onChange={setNewStatus} placeholder="Pilih status" testId="status-select" allowClear={false} />
            <Textarea placeholder="Alasan perubahan (opsional), mis. Pindah ke gereja lain" value={statusNote} onChange={(e) => setStatusNote(e.target.value)} className="rounded-xl" data-testid="status-note" />
            <p className="text-xs text-slate-500">Perubahan akan dicatat atas nama akun Anda yang sedang login.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-xl" onClick={() => setStatusOpen(false)}>Batal</Button>
            <Button className="rounded-xl bg-[hsl(224,64%,33%)]" onClick={changeStatus} disabled={saving} data-testid="confirm-status-btn">
              {saving ? "Menyimpan..." : "Simpan Status"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <FotoLightbox src={zoom?.src} alt={zoom?.alt} onClose={() => setZoom(null)} />
    </div>
  );
}

const ParentRow = ({ label, nama, linked, navigate, testId }) => (
  <div className="flex items-start justify-between gap-3 py-1.5" data-testid={testId}>
    <span className="text-sm text-slate-500">{label}</span>
    <span className="text-right">
      {linked ? (
        <button type="button" onClick={() => navigate(`/jemaat/${linked.id}`)} className="text-sm font-semibold text-[hsl(224,64%,33%)] hover:underline min-h-[32px]" data-testid={`${testId}-link`}>
          {namaGelar(linked)}<span className="block font-mono text-xs text-slate-500 font-normal">NIJ: {linked.nij}</span>
        </button>
      ) : nama ? (
        <span className="text-sm text-slate-800">{nama}{nama !== "****" && <span className="block text-xs text-slate-400">(Belum terdaftar sebagai jemaat)</span>}</span>
      ) : <span className="text-sm text-slate-300">—</span>}
    </span>
  </div>
);

const Chips = ({ items, testId, cls }) => (
  <span className="flex flex-wrap gap-1.5 justify-end" data-testid={testId}>
    {items.map((v) => <span key={v} className={`text-xs px-2.5 py-1 rounded-full font-semibold ${cls}`}>{v}</span>)}
  </span>
);

const BULAN_ID = ["Januari", "Februari", "Maret", "April", "Mei", "Juni", "Juli", "Agustus", "September", "Oktober", "November", "Desember"];
// Info ulang tahun (derived dari tanggal_lahir; 29 Feb tampil 28 Feb pada tahun non-kabisat)
const ulangTahunInfo = (tl) => {
  if (!tl || !/^\d{4}-\d{2}-\d{2}/.test(tl)) return null;
  const m = Number(tl.slice(5, 7)), d = Number(tl.slice(8, 10));
  const now = new Date();
  const leap = (y) => y % 4 === 0 && (y % 100 !== 0 || y % 400 === 0);
  const todayMatch = now.getMonth() + 1 === m && (now.getDate() === d || (m === 2 && d === 29 && now.getDate() === 28 && !leap(now.getFullYear())));
  return { label: todayMatch ? "🎂 Ulang Tahun Hari Ini" : "🎂 Ulang Tahun", value: `${d} ${BULAN_ID[m - 1]}` };
};

const Row = ({ label, value, mono, icon: Icon }) => (
  <div className="flex items-start justify-between gap-4 py-2.5 border-b border-slate-50 last:border-0">
    <span className="text-sm text-slate-500 flex items-center gap-2 shrink-0">
      {Icon && <Icon className="h-4 w-4 text-slate-400" />} {label}
    </span>
    <span className={`text-sm text-slate-800 text-right ${mono ? "font-mono" : ""}`}>
      {value || <span className="text-slate-300">—</span>}
    </span>
  </div>
);
